/** @format */

import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Locale } from "../../features/i18n/init";
import { PDFView } from "../../components/PdfView";
import { useTranslation } from "react-i18next";
import { PaperGroup, usePaperQuestions } from "../../hooks/usePaperQuestions";
import { useBrand } from "../../hooks/useBrand";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useState } from "react";
import { useCart } from "../../contexts/cart/useCart";
import { PaperActions } from "../../components/PaperActions";
import { StructureView } from "../../components/StructureView";
import { PaperEntry, countParts } from "../../utils/paper";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { QuestionPreview } from "../../components/QuestionPreview";
import { trackEvent } from "../../utils/analytics";

interface TabButtonProps {
	activeTab: TabKey;
	onTabChange: (tab: TabKey) => void;
	tabLabels: Record<TabKey, string>;
}
interface TabContentProps {
	activeTab: TabKey;
	groups: PaperGroup[];
	onRemove: (entry: PaperEntry) => void;
	onPreview: (group: PaperGroup) => void;
	onEdit: (group: PaperGroup) => void;
	onReorder: (from: number, to: number) => void;
}

const getTabStyle = (isActive: boolean) =>
	isActive
		? "h-[36px] py-[6px] px-[16px] gap-1 text-[12px] whitespace-nowrap text-(--public-color-btn-add) cursor-pointer border-b-2 var(--public-color-btn-add) font-roboto"
		: "h-[36px] py-[6px] px-4 gap-1 text-[12px] whitespace-nowrap cursor-pointer text-(--public-color-text-default)";

const TAB_KEYS = ["pdfView", "structureView"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TabButtons = ({ activeTab, onTabChange, tabLabels }: TabButtonProps) => {
	const { t } = useTranslation();
	return (
		<div className="flex" role="tablist" aria-label={t("generatedPaper.viewsLabel")}>
			{TAB_KEYS.map((key) => (
				<button
					type="button"
					key={key}
					role="tab"
					aria-selected={activeTab === key}
					onClick={() => onTabChange(key)}
					className={getTabStyle(activeTab === key)}
				>
					{tabLabels[key]}
				</button>
			))}
		</div>
	);
};
const TabContent = ({ activeTab, groups, onRemove, onPreview, onEdit, onReorder }: TabContentProps) => {
	if (activeTab === "pdfView") {
		return (
			<div className="mt-4 px-6">
				<PDFView groups={groups} />
			</div>
		);
	}
	if (activeTab === "structureView") {
		return (
			<div className="mt-4 px-6">
				<StructureView
					groups={groups}
					onRemove={onRemove}
					onPreview={onPreview}
					onEdit={onEdit}
					onReorder={onReorder}
				/>
			</div>
		);
	}
};
const titleStyles =
	"font-lato text-(--public-color-text-default) leading-[150%] font-semibold text-[13px] lg:text-[20px]";

const numberStyles =
	" font-lato text-[15px] lg:text-[25px] leading-[100%] text-(--public-color-text-default) font-black";

export const GeneratedPaperPage = () => {
	const { t } = useTranslation();
	const { locale } = useParams<{ locale: Locale }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { brand } = useBrand();
	const { groups, entryCount, isLoading, error, reload } = usePaperQuestions(brand);
	const { removeQuestion, reorderQuestions } = useCart();

	useDocumentTitle(t("pageTitles.generatedPaper"));

	const [activeTab, setActiveTab] = useState<TabKey>("pdfView");
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [entryToRemove, setEntryToRemove] = useState<PaperEntry | null>(null);
	const [groupToPreview, setGroupToPreview] = useState<PaperGroup | null>(null);

	const totalMarks = groups.reduce((sum, group) => sum + group.entry.totalMarks, 0);
	const duration = totalMarks;
	const parts = countParts(groups.map((group) => group.entry));
	const searchParams = location.search;

	const tabLabels: Record<TabKey, string> = {
		pdfView: t("generatedPaper.pdfView"),
		structureView: t("generatedPaper.structureView"),
	};

	const handleRemove = (entry: PaperEntry) => {
		setEntryToRemove(entry);
		setIsOpen(true);
	};

	const handleConfirmRemove = () => {
		if (entryToRemove) {
			trackEvent("remove_from_paper", { question_id: entryToRemove.parentQuestionId });
			removeQuestion(entryToRemove);
		}
		setIsOpen(false);
		setEntryToRemove(null);
	};

	const handleCancel = () => {
		setIsOpen(false);
		setEntryToRemove(null);
	};

	const handlePreview = (group: PaperGroup) => {
		trackEvent("preview_question", { question_id: group.entry.parentQuestionId });
		setIsOpen(false);
		setGroupToPreview(group);
	};

	const handleEdit = (group: PaperGroup) => {
		trackEvent("edit_paper_question", { question_id: group.entry.parentQuestionId });
		navigate(`/${locale ?? "en"}/questions/${group.entry.parentQuestionId}`, {
			state: { backgroundLocation: location },
		});
	};
	const handleCancelPreview = () => {
		setIsOpen(false);
		setGroupToPreview(null);
	};

	const handleReorder = (from: number, to: number) => {
		trackEvent("reorder_paper_question", { from_index: from, to_index: to });
		reorderQuestions(from, to);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<p className="text-[20px] font-semibold">{t("common.loading")}...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col gap-3 items-center justify-center h-full">
				<p className="text-[20px] font-semibold">{t("generatedPaper.error")}</p>
				<button
					type="button"
					aria-label={t("generatedPaper.retryButton")}
					onClick={reload}
					className="border rounded-md bg-red-600 text-white cursor-pointer w-24 h-8 font-medium font-roboto"
				>
					{t("common.retry")}
				</button>
			</div>
		);
	}

	return (
		<div className="flex flex-col lg:flex-row h-full overflow-y-auto lg:overflow-hidden">
			<div className="w-full lg:w-[75%] flex flex-col lg:h-full">
				<header className="flex flex-row items-center justify-between border-b border-(--public-color-border-default) p-5">
					<div className="flex flex-col gap-4">
						<h1
							data-testid="generated-paper-page-title"
							className="font-lato font-bold text-[20px] lg:text-[30px] leading-[100%] text-(--public-color-text-default)"
						>
							{t("generatedPaper.title")}
						</h1>
						<Link
							to={`/${locale}/search${searchParams}`}
							className="flex flex-row gap-1.5 items-center pl-6 font-lato leading-[175%] align-middle text-[16px] text-(--public-color-text-default)"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-3"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
							</svg>

							{t("generatedPaper.search")}
						</Link>
					</div>

					<div className="flex flex-col md:flex-row gap-5 lg:gap-16.25 px-1.5">
						<div className="flex flex-col gap-1.5">
							<h4 className={titleStyles}>{t("common.questions")}</h4>
							<span className={numberStyles}>{entryCount}</span>
						</div>
						<div className="flex flex-col gap-1.5">
							<h4 className={titleStyles}>{t("generatedPaper.duration")}</h4>
							<span className={numberStyles}>
								{duration} <span className="text-[10px] lg:text-[15px]">({t("generatedPaper.minutes")})</span>
							</span>
						</div>

						<div className="flex flex-col gap-1.5">
							<h4 className={titleStyles}>{t("common.marks")}</h4>
							<span className={numberStyles}>{totalMarks}</span>
						</div>

						<div className="flex flex-col gap-1.5">
							<h4 className={titleStyles}>{t("generatedPaper.parts")}</h4>
							<span className={numberStyles}>
								{parts.selected}/{parts.total}
							</span>
						</div>
					</div>
				</header>

				<div className="flex flex-col flex-1 min-h-0">
					<div className="h-10">
						<TabButtons
							activeTab={activeTab}
							onTabChange={(tab) => {
								trackEvent("view_paper_tab", { tab });
								setActiveTab(tab);
							}}
							tabLabels={tabLabels}
						/>
					</div>
					<div className="flex-1 lg:overflow-y-auto min-h-0">
						<TabContent
							activeTab={activeTab}
							groups={groups}
							onRemove={handleRemove}
							onPreview={handlePreview}
							onEdit={handleEdit}
							onReorder={handleReorder}
						/>
					</div>
				</div>
			</div>

			<div className="w-full lg:w-[25%] flex justify-center">
				<PaperActions groups={groups} title={t("generatedPaper.title")} />
			</div>

			{isOpen && (
				<ConfirmationDialog
					onConfirm={handleConfirmRemove}
					onCancel={handleCancel}
					title={t("confirmationDialog.deleteTitle")}
					message={t("confirmationDialog.deleteMessage")}
					btnDeleteMsg={t("confirmationDialog.delete")}
				/>
			)}

			{groupToPreview && <QuestionPreview group={groupToPreview} onCancel={handleCancelPreview} />}
		</div>
	);
};
